import {Dispatch, FC, SetStateAction} from 'react'

import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {DiscountCoupon} from '@/components/common/form/discount-coupon/DiscountCoupon'
import {VerifiedDiscountCoupon} from '@/components/common/form/discount-coupon/VerifiedDiscountCoupon'
import {usePreferences} from '@/components/context/Preferences'
import {useFormSummaryContent} from '@/content/formSummary'
import {useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {REST_METHODS} from '@/utils/api/types'
import {DiscountCouponEntity} from '@/utils/constants'
import {Currency} from '@/utils/currency'
import {DiscountCouponChange} from '@/utils/discount-coupon/types'
import {DiscountCouponFragment} from '@/utils/graphqlSdk'
import {BasePrice, endToEndGuaranteePercentage} from '@/utils/pricing'
import {routeTo} from '@/utils/router'

import {FormTooltip} from '../../tooltip/FormTooltip'
import {FormSummaryItem} from '../common/layout/FormSummaryItem'
import {FormSummaryItemBreakdownRow} from '../common/layout/FormSummaryItemBreakdownRow'
import {FormSummarySection} from '../common/layout/FormSummarySection'
import {FormSummaryTotalPrice} from './FormSummaryTotalPrice'

type FormSummaryPriceWrapperProps = {
  requestId?: string | null
  totalPrice?: BasePrice
  estimatedPrice?: number
  priceTitle?: string
  discountCoupon?: DiscountCouponFragment | null
  setDiscountCoupon?: Dispatch<SetStateAction<DiscountCouponFragment | null>>
  discountCouponPrice?: number
  discountCouponPriceBreakdown?: {title: string; price: number}[]
  hasAppliedDiscountCoupon?: boolean
  hasDiscountInput?: boolean
  multiCountryDiscount?: number
  couponId?: string
  currency?: Currency
  e2eProtectionPrice?: number
  removeE2eProtection?: () => void
  brandProtectionPrice?: string
  removeBrandProtection?: () => void
  priceNote?: string
}

export const FormSummaryPriceWrapper: FC<FormSummaryPriceWrapperProps> = ({
  requestId,
  totalPrice,
  estimatedPrice,
  priceTitle,
  discountCoupon,
  setDiscountCoupon,
  discountCouponPrice,
  discountCouponPriceBreakdown,
  hasAppliedDiscountCoupon,
  hasDiscountInput,
  multiCountryDiscount,
  couponId,
  currency: currencyOverride,
  e2eProtectionPrice,
  removeE2eProtection,
  brandProtectionPrice,
  removeBrandProtection,
  priceNote,
}) => {
  const {t} = useTranslation()
  const {currency: currencyPreferences} = usePreferences()
  const currency = currencyOverride ?? currencyPreferences

  const {
    estimatedPriceTitle,
    multiCountryDiscountTitle,
    serviceFeeDiscountTitle,
    endToEndProtectionTitle,
    endToEndGuaranteeTitle,
    endToEndGuaranteeTooltip,
    additionalServiceTitle,
    brandMonitoringTitle,
  } = useFormSummaryContent()

  const handleDiscountCouponChanged = ({coupon}: DiscountCouponChange) => {
    setDiscountCoupon?.(coupon)
  }

  const applyDiscountCoupon = async ({
    discountCouponId,
  }: {
    discountCouponId: string
  }): Promise<DiscountCouponChange | null> => {
    if (!requestId) {
      return null
    }
    const response = await fetch(routeTo.api.request.applyCoupon(requestId), {
      method: REST_METHODS.PUT,
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({discount_coupon_id: discountCouponId}),
    })
    const data = await response.json()
    return data ? {coupon: data.coupon ?? null} : null
  }

  return (
    <>
      {!!multiCountryDiscount && (
        <FormSummarySection label={t(multiCountryDiscountTitle)}>
          <FormSummaryItem
            title={t(serviceFeeDiscountTitle)}
            price={multiCountryDiscount}
            currency={currency}
          />
        </FormSummarySection>
      )}
      {!!brandProtectionPrice && !!removeBrandProtection && (
        <FormSummarySection label={t(additionalServiceTitle)}>
          <FormSummaryItem
            title={t(brandMonitoringTitle)}
            description={brandProtectionPrice}
            currency={currency}
            onRemove={hasDiscountInput ? () => removeBrandProtection() : undefined}
          />
        </FormSummarySection>
      )}
      {hasAppliedDiscountCoupon && discountCouponPrice && (
        <VerifiedDiscountCoupon
          removeDiscountCouponUrl={requestId && routeTo.api.request.coupon(requestId)}
          couponId={(couponId || discountCoupon?.id) ?? ''}
          price={discountCouponPrice}
          currency={currency}
          onDiscountCouponChanged={handleDiscountCouponChanged}
          allowRemoval={hasDiscountInput}
        >
          {discountCouponPriceBreakdown && (
            <ul className="list">
              {discountCouponPriceBreakdown?.map(({title, price}, i) => (
                <FormSummaryItemBreakdownRow
                  key={i}
                  title={title}
                  price={price}
                  currency={currency}
                />
              ))}
            </ul>
          )}
        </VerifiedDiscountCoupon>
      )}
      {!!e2eProtectionPrice && !!removeE2eProtection && (
        <FormSummarySection label={t(endToEndProtectionTitle, {ns: 'verification-result'})}>
          <FormSummaryItem
            title={t(endToEndGuaranteeTitle, {ns: 'verification-result'})}
            price={e2eProtectionPrice}
            tooltip={
              <FormTooltip
                tooltipText={t(endToEndGuaranteeTooltip, {
                  percentage: endToEndGuaranteePercentage,
                  ns: 'verification-result',
                })}
              />
            }
            currency={currency}
            onRemove={hasDiscountInput ? () => removeE2eProtection() : undefined}
          />
        </FormSummarySection>
      )}
      <div className="divider">
        {totalPrice ? (
          <FormSummaryTotalPrice
            priceVatBase={totalPrice.vatBase}
            priceVat={totalPrice.vat}
            priceWithVat={totalPrice.withVat}
            currency={currency}
          />
        ) : estimatedPrice !== undefined ? (
          <FormSummaryItem
            title={priceTitle || t(estimatedPriceTitle)}
            price={estimatedPrice}
            fontWeight={TextFontWeight.bold}
            textColor={theme.palette.text.default}
            hasDecimal={estimatedPrice > 0}
            currency={currency}
          />
        ) : null}
      </div>
      {!hasAppliedDiscountCoupon && hasDiscountInput && setDiscountCoupon && (
        <div className="divider discount-input">
          <DiscountCoupon
            applyDiscountCoupon={requestId ? applyDiscountCoupon : undefined}
            onDiscountCouponChanged={handleDiscountCouponChanged}
            entityType={DiscountCouponEntity.registrationRequest}
          />
        </div>
      )}
      {priceNote && (
        <div className="divider note">
          <Text variant={TextVariant.caption} element="p" align="center">
            {priceNote}
          </Text>
        </div>
      )}

      <style jsx>{`
        .divider {
          padding-top: ${theme.spacing[3]};
          margin-top: ${theme.spacing[3]};
          border-top: 1px solid ${theme.palette.gray[200]};
        }
        .discount-input {
          padding-top: ${theme.spacing[6]};
        }
        .list {
          margin-top: ${theme.spacing[2]};
        }
        .note {
          padding-inline: ${theme.spacing[3]};
        }
      `}</style>
    </>
  )
}
